import { useState, useCallback } from 'react';
import { dockerService } from '../services/dockerService';

/**
 * useContainerActions Hook
 * Provides container control actions (start/stop/restart/etc)
 * @param {string} containerId - Container ID
 * @param {function} onSuccess - Callback after successful action
 */
export function useContainerActions(containerId, onSuccess) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const performAction = useCallback(async (actionFn, actionName) => {
    try {
      setLoading(true);
      setError(null);
      await actionFn();

      if (onSuccess) {
        onSuccess(actionName);
      }
    } catch (err) {
      console.error(`${actionName} failed:`, err);
      setError(err.message);
      throw err; // Re-throw so components can handle errors
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);

  const start = useCallback(() => {
    return performAction(
      () => dockerService.startContainer(containerId),
      'start'
    );
  }, [containerId, performAction]);

  const stop = useCallback(() => {
    return performAction(
      () => dockerService.stopContainer(containerId),
      'stop'
    );
  }, [containerId, performAction]);

  const restart = useCallback(() => {
    return performAction(
      () => dockerService.restartContainer(containerId),
      'restart'
    );
  }, [containerId, performAction]);

  const pause = useCallback(() => {
    return performAction(
      () => dockerService.pauseContainer(containerId),
      'pause'
    );
  }, [containerId, performAction]);

  const unpause = useCallback(() => {
    return performAction(
      () => dockerService.unpauseContainer(containerId),
      'unpause'
    );
  }, [containerId, performAction]);

  return {
    loading,
    error,
    start,
    stop,
    restart,
    pause,
    unpause
  };
}
